/** @format */

import { finalize, map, retry, shareReplay, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ArticleSearchV2Response, Article } from '@shared/data-access/interfaces';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ProTipsDataService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  // De-dupes concurrent in-flight requests made during the same server render.
  private readonly inFlightRequests = new Map<string, Observable<Article[]>>();

  getProTipsData$(
    categoryName: string,
    chainIdentifierAbbr: string,
    noOfPages = 8,
    retries = 0,
  ): Observable<Article[]> {
    const cacheKey = `${chainIdentifierAbbr}-${categoryName}-${noOfPages}`;
    const stateKey = makeStateKey<Article[]>(`pro-tips-${cacheKey}`);

    const cachedData = this.transferState.get<Article[] | null>(stateKey, null);
    if (cachedData) {
      return of(cachedData);
    }

    // On the server, reuse an already in-flight request for the same params instead of
    // firing a second HTTP call (e.g. the resolver's prefetch and the component's own
    // fetch racing for the same category before the first response has resolved).
    if (this.isServer) {
      const inFlightReq = this.inFlightRequests.get(cacheKey);
      if (inFlightReq) {
        return inFlightReq;
      }
    }

    const url = `https://mtools-article-search.dickssportinggoods.com/article-search/v2/${chainIdentifierAbbr}?term=${categoryName}&pageSize=${noOfPages}&relevance=true&shouldProvidePlaceholderResults=true`;

    const request$ = this.http.get<ArticleSearchV2Response>(url).pipe(
      map((response) => response?.responseList || []),
      tap((res) => this.transferState.set(stateKey, res)),
      retry(retries),
      finalize(() => this.inFlightRequests.delete(cacheKey)),
      shareReplay(1),
    );

    if (this.isServer) {
      this.inFlightRequests.set(cacheKey, request$);
    }

    return request$;
  }
}
